// src/pages/NewHomePage.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FaBook, FaChild, FaHeart, FaImage, FaMagic, FaRegCommentDots, FaRegEye, FaTruck, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { SUPPORT_NEED_ROUTE_PARAMS } from '../config/storybookOptions';

const asArray = (value) => (Array.isArray(value) ? value : []);
const HERO_IMAGE_SRC = '/images/book-covers/home_page.png';
const PRINTED_BOOKS_IMAGE_SRC = '/images/book-covers/printed_books.jpeg';
const FAMILY_IMAGE_SOURCES = [
  '/images/book-covers/reading_moment.png',
  '/images/book-covers/books.png'
];
const TRANSFORMATION_IMAGE_SOURCES = [
  '/images/book-covers/ali_real.png',
  '/images/book-covers/ali_title.png',
  '/images/book-covers/ali_page.png',
  '/images/book-covers/ali_page_2.png',
  '/images/book-covers/ali_page_3.png'
];
const needStoryIcons = [FaChild, FaHeart, FaUsers];
const howStepIcons = [FaRegCommentDots, FaMagic, FaRegEye];
const howDeliverableIcons = [FaBook, FaChild, FaTruck, FaImage];
const heroNeedRouteParams = [
  SUPPORT_NEED_ROUTE_PARAMS.startingSchool,
  SUPPORT_NEED_ROUTE_PARAMS.siblingJealousy,
  SUPPORT_NEED_ROUTE_PARAMS.sleepRoutine,
  SUPPORT_NEED_ROUTE_PARAMS.fearAnxiety,
  SUPPORT_NEED_ROUTE_PARAMS.confidence,
  SUPPORT_NEED_ROUTE_PARAMS.sharingFriendship,
  SUPPORT_NEED_ROUTE_PARAMS.bigFeelings,
  SUPPORT_NEED_ROUTE_PARAMS.customNeed
];

function NewHomePage() {
  const { t } = useTranslation();
  const [activeFaqIndex, setActiveFaqIndex] = useState(null);
  const [selectedHeroNeed, setSelectedHeroNeed] = useState(null);

  const needItems = asArray(t('home.needSelector.items', { returnObjects: true }));
  const needStoryCards = asArray(t('home.needStory.cards', { returnObjects: true })).slice(0, 3);
  const howSteps = asArray(t('home.howItWorks.steps', { returnObjects: true })).slice(0, 3);
  const howDeliverables = t('home.howItWorks.deliverables', { returnObjects: true });
  const howDeliverableItems = asArray(howDeliverables.items).slice(0, 4);
  const familyPerspective = t('home.familyPerspective', { returnObjects: true });
  const familyProofCards = asArray(familyPerspective.proofCards).slice(0, 3);
  const familyImages = asArray(familyPerspective.images).slice(0, 2);
  const exampleTransformation = t('home.examples.transformation', { returnObjects: true });
  const transformationImages = asArray(exampleTransformation.images).slice(0, 5);
  const corporate = t('home.corporate', { returnObjects: true });
  const faqItems = asArray(t('home.faq.items', { returnObjects: true }));
  const selectedHeroNeedIndex = needItems.indexOf(selectedHeroNeed);
  const selectedHeroNeedRouteParam = heroNeedRouteParams[selectedHeroNeedIndex] || '';
  const selectedHeroNeedCreateUrl = selectedHeroNeedRouteParam
    ? `/create-story?need=${encodeURIComponent(selectedHeroNeedRouteParam)}`
    : '/create-story';

  const toggleFaq = (index) => {
    setActiveFaqIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <div className="home-page home-v2">
      <section className="home-v2-hero">
        <div className="container home-v2-hero-grid">
          <div className="home-v2-hero-copy">
            <p className="home-v2-eyebrow">
              <span aria-hidden="true" />
              {t('home.heroEyebrow')}
            </p>
            <h1>{t('home.heroTitle')}</h1>
            <p className="home-v2-lede">{t('home.heroSubtitle')}</p>

            <div className="home-v2-actions">
              <Link to="/create-story" className="home-v2-primary-cta">
                {t('home.heroPrimaryCta')}
              </Link>
              <Link to="/features#how-it-works" className="home-v2-secondary-link">
                {t('home.heroSecondaryCta')}
              </Link>
            </div>

            <div className="home-v2-hero-need-selector" aria-labelledby="home-hero-need-title">
              <p id="home-hero-need-title">{t('home.needSelector.title')}</p>
              <div className="home-v2-hero-need-list">
                {needItems.map((need) => (
                  <button
                    key={need}
                    type="button"
                    className={`home-v2-hero-need-chip${selectedHeroNeed === need ? ' is-selected' : ''}`}
                    aria-pressed={selectedHeroNeed === need}
                    onClick={() => setSelectedHeroNeed((currentNeed) => (currentNeed === need ? null : need))}
                  >
                    {need}
                  </button>
                ))}
              </div>
              {selectedHeroNeed && (
                <div className="home-v2-selected-need" aria-live="polite">
                  <span>{t('home.needSelector.selectedLabel')}</span>
                  <strong>{selectedHeroNeed}</strong>
                  <Link to={selectedHeroNeedCreateUrl}>
                    {t('home.needSelector.selectedCta')}
                  </Link>
                </div>
              )}
            </div>
          </div>

          <div className="home-v2-visual">
            <img
              src={HERO_IMAGE_SRC}
              alt={t('home.heroImageAlt')}
              className="home-v2-hero-image"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-needs">
        <div className="container">
          <div className="home-v2-need-story-header">
            <h2>{t('home.needStory.title')}</h2>
            <p>
              {t('home.needStory.subtitleBefore')}
              <span className="home-v2-need-story-highlight">{t('home.needStory.subtitleHighlight')}</span>
              {t('home.needStory.subtitleAfter')}
            </p>
          </div>

          <div className="home-v2-need-story-grid">
            {needStoryCards.map((card, index) => {
              const NeedIcon = needStoryIcons[index] || FaHeart;

              return (
                <Link key={card.title} to="/features" className="home-v2-need-story-card">
                  <span className="home-v2-need-story-icon" aria-hidden="true">
                    <NeedIcon />
                  </span>
                  <span>
                    <strong>{card.title}</strong>
                    <small>{card.description}</small>
                  </span>
                </Link>
              );
            })}
          </div>

          <div className="home-v2-section-action">
            <Link to="/features" className="home-v2-text-cta">
              {t('home.needStory.cta')}
            </Link>
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-how">
        <div className="container">
          <div className="home-v2-section-header">
            <h2>{t('home.howItWorks.title')}</h2>
            <p>{t('home.howItWorks.subtitle')}</p>
          </div>

          <div className="home-v2-card-grid home-v2-steps-grid">
            {howSteps.map((step, index) => {
              const StepIcon = howStepIcons[index] || FaRegEye;

              return (
                <article key={step.number} className="home-v2-card home-v2-step-card">
                  <div className="home-v2-step-card-top">
                    <span className="home-v2-step-icon" aria-hidden="true">
                      <StepIcon />
                    </span>
                    <span className="home-v2-step-number">{step.number}</span>
                  </div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </article>
              );
            })}
          </div>

          <div className="home-v2-how-deliverables">
            <div>
              <h3>{howDeliverables.title}</h3>
              <div className="home-v2-how-deliverables-list">
                {howDeliverableItems.map((item, index) => {
                  const DeliverableIcon = howDeliverableIcons[index] || FaBook;

                  return (
                    <div key={item.title} className="home-v2-how-deliverable-item">
                      <span className="home-v2-how-deliverable-icon" aria-hidden="true">
                        <DeliverableIcon />
                      </span>
                      <span>
                        <strong>{item.title}</strong>
                        <small>{item.description}</small>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="home-v2-hardcopy-image-frame">
              <img
                src={PRINTED_BOOKS_IMAGE_SRC}
                alt={howDeliverables.imageAlt}
                className="home-v2-hardcopy-image"
                loading="lazy"
              />
            </div>
          </div>

          <div className="home-v2-section-action">
            <Link to="/features#how-it-works" className="home-v2-text-cta">
              {t('home.howItWorks.cta')}
            </Link>
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-family-perspective">
        <div className="container">
          <div className="home-v2-section-header">
            <h2>{t('home.familyPerspective.title')}</h2>
            <p>{t('home.familyPerspective.subtitle')}</p>
          </div>

          <div className="home-v2-family-proof">
            <div className="home-v2-family-moment-grid">
              {familyProofCards.map((card) => (
                <article key={card.text} className="home-v2-family-moment">
                  <span className="home-v2-family-proof-icon" aria-hidden="true">
                    <FaRegCommentDots />
                  </span>
                  <span>
                    <strong>{card.title}</strong>
                    <p>{card.text}</p>
                  </span>
                </article>
              ))}
            </div>

            <div className="home-v2-family-image-grid" aria-label={familyPerspective.imageLabel}>
              {familyImages.map((image, index) => (
                <figure key={image.alt} className={`home-v2-family-image-card home-v2-family-image-card-${index + 1}`}>
                  <img
                    src={FAMILY_IMAGE_SOURCES[index]}
                    alt={image.alt}
                    loading="lazy"
                  />
                </figure>
              ))}
            </div>
          </div>

          <p className="home-v2-family-privacy-note">{familyPerspective.privacyNote}</p>

          <div className="home-v2-section-action">
            <Link to="/examples" className="home-v2-text-cta">
              {t('home.familyPerspective.cta')}
            </Link>
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-transformation">
        <div className="container">
          <h2 className="home-v2-transformation-title">{exampleTransformation.title}</h2>

          <div className="home-v2-transformation-flow">
            <div className="home-v2-ali-input-row">
              <figure className="home-v2-ali-real-card">
                <img
                  src={TRANSFORMATION_IMAGE_SOURCES[0]}
                  alt={transformationImages[0]?.alt || ''}
                  loading="lazy"
                />
              </figure>

              <div className="home-v2-need-card">
                <span className="home-v2-transformation-label">{exampleTransformation.needLabel}</span>
                <span className="home-v2-need-card-icon" aria-hidden="true">
                  <FaRegCommentDots />
                </span>
                <p>{exampleTransformation.needQuote}</p>
              </div>
            </div>

            <div className="home-v2-ali-arrow">
              <span aria-hidden="true">↓</span>
              <strong>{exampleTransformation.connector}</strong>
            </div>

            <div className="home-v2-ali-output-grid" aria-label={exampleTransformation.previewLabel}>
              {transformationImages.slice(1).map((image, index) => (
                <figure
                  key={image.alt}
                  className={`home-v2-ali-output-card home-v2-ali-output-card-${index + 1}`}
                >
                  <img
                    src={TRANSFORMATION_IMAGE_SOURCES[index + 1]}
                    alt={image.alt}
                    loading="lazy"
                  />
                </figure>
              ))}
            </div>
          </div>

          <div className="home-v2-section-action">
            <Link to="/examples" className="home-v2-text-cta">
              {exampleTransformation.cta}
            </Link>
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-corporate">
        <div className="container">
          <div className="home-v2-corporate-inline">
            <p className="home-v2-corporate-line">
              {corporate.titleBefore}
              <span className="home-v2-corporate-highlight">{corporate.titleHighlight}</span>
              {corporate.titleAfter}
            </p>
            <Link to="/contact#corporate" className="home-v2-text-cta">
              {corporate.cta}
            </Link>
          </div>
        </div>
      </section>

      <section className="home-v2-section home-v2-faq">
        <div className="container">
          <div className="home-v2-section-header">
            <h2>{t('home.faq.title')}</h2>
          </div>
          <div className="home-v2-faq-list">
            {faqItems.map((item, index) => {
              const isOpen = activeFaqIndex === index;

              return (
                <div key={item.question} className="home-v2-faq-item">
                  <button type="button" onClick={() => toggleFaq(index)} aria-expanded={isOpen}>
                    <span>{item.question}</span>
                    <span aria-hidden="true">{isOpen ? '-' : '+'}</span>
                  </button>
                  {isOpen && <p>{item.answer}</p>}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}

export default NewHomePage;
